import { DataKeys } from "../../types";
import problemVisualImg from "../../image/health/problem-doctor-fragmented-workflow.png";
import { useState } from "react";
import { useLang } from "../Context";
import { SlideInAnim } from "../tools/Animations";
import { HealthSection } from "./HealthSection";
import { HealthSectionHeader } from "./HealthSectionHeader";
import { getLocalized, HealthLang } from "./healthTypes";
import {
    healthCardBody,
    healthCardTitle,
    healthProblemEditorialFallback,
    healthProblemEditorialImage,
    healthProblemEditorialImageFrame,
    healthProblemInsightCard,
    healthProblemInsightIndex,
} from "./healthVisualStyles";

interface HealthProblemSectionProps {
    data: DataKeys;
}

export function HealthProblemSection({ data }: HealthProblemSectionProps) {
    const [imageFailed, setImageFailed] = useState(false);
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const title = getLocalized(data?.title, hLang);
    const intro = getLocalized(data?.intro, hLang);
    const cards: DataKeys[] = data?.cards ?? [];
    const sectionId = "problem";
    const visualImageAlt = "Doctor handling fragmented clinical workflow data";

    return (
        <HealthSection id={sectionId} ariaLabelledBy={`${sectionId}-title`} compact>
            <div className="w-full min-w-0">
                <HealthSectionHeader
                    title={title}
                    titleId={`${sectionId}-title`}
                    subtitle={intro}
                    subtitleWide
                />
                <div className="grid w-full min-w-0 grid-cols-1 gap-7 lg:grid-cols-[minmax(0,0.49fr)_minmax(0,0.51fr)] lg:items-stretch lg:gap-11">
                    <SlideInAnim direction="left">
                        <div className={healthProblemEditorialImageFrame}>
                            {!imageFailed ? (
                                <img
                                    src={problemVisualImg}
                                    alt={visualImageAlt}
                                    className={healthProblemEditorialImage}
                                    loading="lazy"
                                    onError={() => setImageFailed(true)}
                                />
                            ) : (
                                <div className={healthProblemEditorialFallback} />
                            )}
                        </div>
                    </SlideInAnim>
                    <div className="grid w-full min-w-0 grid-cols-1 gap-3 text-left [text-align:left] md:grid-cols-2 md:gap-4 lg:grid-cols-1 lg:content-start lg:gap-3">
                        {cards.map((card, index) => (
                            <div key={index} className={healthProblemInsightCard}>
                                <p className={healthProblemInsightIndex}>
                                    {String(index + 1).padStart(2, "0")}
                                </p>
                                <h3
                                    className={`${healthCardTitle} mt-1 self-start text-left leading-snug [text-align:left] text-base lg:text-[1.06rem]`}
                                >
                                    {getLocalized(card.title, hLang)}
                                </h3>
                                <p
                                    className={`${healthCardBody} mt-1.5 self-start text-left leading-relaxed [text-align:left]`}
                                >
                                    {getLocalized(card.text, hLang)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </HealthSection>
    );
}
